import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';
import type { UserProgress, Achievement, Word } from '../types';
interface DashboardStats {
  totalWords: number;
  averageScore: number;
  streakDays: number;
  totalSessions: number;
}


interface DashboardData {
  stats: DashboardStats;
  recentProgress: (UserProgress & { words: Pick<Word, 'word' | 'category'> })[];
  unlockedAchievements: (Achievement & { achievement_details: { name: string; description: string; icon: string; } })[];
  wordOfTheDay: Word | null;
  learningRecommendations: Word[];
}
const fetchDashboardData = async (userId: string): Promise<DashboardData> => {
  // Parallelize fetches
  const [
    statsRes,
    progressRes,
    achievementsRes,
    wordOfTheDayRes,
    recommendationsRes
  ] = await Promise.all([
    supabase.rpc('get_user_stats', { p_user_id: userId }),
    supabase.from('user_progress').select('id, created_at, best_score, words(word, category)').eq('user_id', userId).order('created_at', { ascending: false }).limit(5),
    supabase.from('user_achievements').select('id, earned_at, achievement_details:achievements(name, description, icon)').eq('user_id', userId).order('earned_at', { ascending: false }).limit(4),
    supabase.rpc('get_word_of_the_day', { p_user_id: userId }),
    supabase.rpc('get_learning_recommendations', { p_user_id: userId, p_limit: 3 })
  ]);

  if (statsRes.error) throw new Error(`Stats: ${statsRes.error.message}`);
  if (progressRes.error) throw new Error(`Progress: ${progressRes.error.message}`);
  if (achievementsRes.error) throw new Error(`Achievements: ${achievementsRes.error.message}`);
  if (wordOfTheDayRes.error) throw new Error(`Word of the Day: ${wordOfTheDayRes.error.message}`);
  if (recommendationsRes.error) throw new Error(`Recommendations: ${recommendationsRes.error.message}`);

  const statsData = statsRes.data[0];

  return {
    stats: {
      totalWords: statsData?.total_words ?? 0,
      averageScore: (statsData?.average_score ?? 0)  * 100,
      streakDays: statsData?.streak_days ?? 0,
      totalSessions: statsData?.total_sessions ?? 0,
    },
    recentProgress: progressRes.data as any[] ?? [],
    unlockedAchievements: achievementsRes.data as any[] ?? [],
    wordOfTheDay: wordOfTheDayRes.data?.[0] ? { ...wordOfTheDayRes.data[0], created_at: null, updated_at: null } as Word : null,
    learningRecommendations: recommendationsRes.data?.map(item => ({ ...item, created_at: null, updated_at: null })) as Word[] ?? [],
  };
};

export const useDashboard = () => {
  const { user } = useAuthStore();
  return useQuery<DashboardData, Error>({
    queryKey: ['dashboard', user?.id],
    queryFn: () => fetchDashboardData(user!.id),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};